import { Prefab, Layers, RenderTexture, EventTarget, instantiate, director, Director, find } from 'cc';
import PostProcessing from './PostProcessing';

export type SnapshotParams =
    | {
          prefab: Prefab;
          priority: number;
          postProcessing?: PostProcessing;
      }
    | {
          postProcessing: PostProcessing;
          priority: number;
      };

export type ISnapshotSetting = {
    /** 每幾幀進行幾次快照(Layer不夠設置在大也沒用) */
    timesPerFrame: number;
};

export default class SnapshotManager extends EventTarget {
    private readonly _textureMap: Map<string, RenderTexture> = new Map();
    private readonly _taskQueue: SnapshotParams[] = [];
    private _setting: ISnapshotSetting = {
        timesPerFrame: 5,
    };

    constructor(config?: Partial<ISnapshotSetting>) {
        super();
        if (config) {
            this._setting = { ...this._setting, ...config };
        }
        director.on(Director.EVENT_BEFORE_UPDATE, this._runQueue, this);
    }

    public getCache(key: string): RenderTexture | undefined {
        return this._textureMap.get(key);
    }

    public setCache(key: string, texture: RenderTexture) {
        const cache = this._textureMap.get(key);
        if (cache === texture) {
            return;
        }
        this._textureMap.set(key, texture);
        this.emit(key, texture);
    }

    public queue(postProcessing: SnapshotParams | SnapshotParams[]) {
        this._taskQueue.push(...(Array.isArray(postProcessing) ? postProcessing : [postProcessing]));
        this._taskQueue.sort((a, b) => a.priority - b.priority);
    }

    private async _runQueue() {
        const { timesPerFrame } = this._setting;
        for (let i = 0; i < timesPerFrame; i++) {
            const isBreak = this._processTask();
            if (isBreak) {
                break;
            }
        }
    }

    private _processTask(): boolean {
        if (this._taskQueue.length <= 0) {
            return true;
        }

        const task = this._taskQueue.shift();

        let postProcessing: PostProcessing;
        const hasPrefab = 'prefab' in task;
        if (hasPrefab) {
            const node = instantiate(task.prefab);
            postProcessing = node.getComponent(PostProcessing);
            if (!postProcessing) {
                postProcessing = node.getComponentInChildren(PostProcessing);
                if (postProcessing) {
                    throw new Error('Prefab contains PostProcessing component in children, please use PostProcessing directly.');
                }
                throw new Error('Prefab does not contain PostProcessing component.');
            }
            postProcessing.snapshotConfig.isQueued = true;
        } else {
            ({ postProcessing } = task);
        }

        if (postProcessing.requiredLayerCount > Layers.unusedLayerCount) {
            // wait for next frame until there are enough available layers
            this._taskQueue.unshift(task);
            return true;
        }

        if (hasPrefab) {
            postProcessing.node.setParent(find('Canvas'));
        }
        postProcessing.snapshot();
        return false;
    }
}
